import React, { memo, useState, useEffect } from "react";
import styled from "@emotion/styled";
import { CardNumber } from "../CardNumber";
import { CardPin } from "../CardPin";
import { CardBalance } from "../Balance";
import { RefId } from "../Ref";
import { SecondaryButton } from "../../../Button";
import { Icon } from "ccui-framework";
import { useDispatch, useSelector } from "react-redux";
import { updateCard } from "../../thunk";
import { merchantsById } from "../../../Merchants";
import PropTypes from "prop-types";
import { LOADING_EMUNS } from "../../constants";
import { setLoadingState } from "../../action";

const Td = styled.td`
  text-align: center;
  padding: 6px;
  font-size: 11px;
  color: #000;
  font-weight: normal;

  &:first-of-type {
    text-align: left;
  }
`;

const Img = styled.img`
  height: 40px;
  cursor: pointer;
  margin-right: 10px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Edit = memo(
  ({ card, setMyEditCardErr, myEditCardErr, cancelEditing }) => {
    const dispatch = useDispatch();
    const merchants = useSelector(merchantsById);
    const initCard = {
      selectedMerchant: merchants[card.merchant],
      balance: card.enterValue + "",
      refId: card.refId ?? "",
      pin: card.pin ?? "",
      number: card.number ?? "",
    };
    const [myCard, setMyCard] = useState(initCard);
    const formatCurrency = (amount) =>
      new Intl.NumberFormat("en-US", {
        currencySign: "accounting",
        style: "currency",
        currency: "USD",
      }).format(amount);

    useEffect(() => {
      dispatch(setLoadingState(card.id, LOADING_EMUNS.DEFAULT));
    }, []);

    const resetErrors = () => {
      setMyEditCardErr({ error: false, msg: "" });
    };

    const submit = async (e) => {
      e.preventDefault();

      if (!myEditCardErr) {
        if (JSON.stringify(initCard) === JSON.stringify(myCard)) {
          cancelEditing();
        } else {
          dispatch(
            updateCard(
              {
                card: {
                  merchantId: myCard.selectedMerchant.id,
                  enterValue: Number(myCard.balance),
                  number: myCard.number,
                  pin: myCard.pin,
                  refId: myCard.refId,
                },
              },
              card.id
            )
          );
        }
      }
    };

    return (
      <>
        <Td>
          <Img src={merchants[card.merchant].image ?? ""} />
          {merchants[card.merchant].name ?? ""}
        </Td>
        <Td>
          <CardNumber
            maxLength={10}
            merchantSelectorFocus={() => {}}
            hasMerchant={!!myCard.selectedMerchant}
            defaultValue={myCard.number}
            validation={myCard?.selectedMerchant?.validation ?? ""}
            onChange={(field, value, error, msg) => {
              if (error) {
                setMyEditCardErr({
                  error: error,
                  msg,
                });
              } else {
                if (value) {
                  setMyCard((card) => ({ ...card, [field]: value }));
                  resetErrors();
                }
              }
            }}
          />
        </Td>
        <Td>
          <CardPin
            maxLength={4}
            merchantSelectorFocus={() => {}}
            hasMerchant={!!myCard.selectedMerchant}
            defaultValue={myCard.pin}
            validation={myCard?.selectedMerchant?.validation ?? ""}
            onChange={(field, value, error, msg) => {
              if (error) {
                setMyEditCardErr({
                  error: error,
                  msg,
                });
              } else {
                if (value) {
                  setMyCard((card) => ({ ...card, [field]: value }));
                  resetErrors();
                }
              }
            }}
          />
        </Td>
        <Td>
          <RefId
            maxLength={3}
            merchantSelectorFocus={() => {}}
            hasMerchant={!!myCard.selectedMerchant}
            defaultValue={myCard.refId}
            onChange={(e) => {
              const value = e.target.value.trim();
              setMyCard((card) => ({ ...card, refId: value }));
              resetErrors();
            }}
          />
        </Td>
        <Td>
          <CardBalance
            maxLength={myCard.selectedMerchant.limit.max.toString().length - 2}
            merchantSelectorFocus={() => {}}
            hasMerchant={!!myCard.selectedMerchant}
            defaultValue={myCard.balance}
            min={myCard?.selectedMerchant?.limit?.min ?? 0}
            max={myCard?.selectedMerchant?.limit?.max ?? 0}
            onChange={(field, value, error, msg) => {
              if (error) {
                setMyEditCardErr({
                  error: true,
                  msg,
                });
              } else {
                if (value) {
                  setMyCard((card) => ({ ...card, [field]: value }));
                  resetErrors();
                }
              }
            }}
          />
        </Td>
        <Td>{card.percentage}%</Td>
        <Td>{formatCurrency(card.cashValue)}</Td>
        <Td>
          {merchants[card.merchant].submissionMethod.charAt(0).toUpperCase() +
            merchants[card.merchant].submissionMethod.slice(1)}
        </Td>
        <Td>
          <Actions>
            <SecondaryButton onClick={submit}>
              <Icon name="fa-check" />
            </SecondaryButton>
          </Actions>
        </Td>
      </>
    );
  }
);

const EditErr = ({ error }) => {
  return (
    <Td colSpan={9}>
      <p className="errorMsg">{error}</p>
    </Td>
  );
};

Edit.propTypes = {
  card: PropTypes.object.isRequired,
  setMyEditCardErr: PropTypes.func.isRequired,
  myEditCardErr: PropTypes.bool.isRequired,
  cancelEditing: PropTypes.func.isRequired,
};

EditErr.propTypes = {
  error: PropTypes.string.isRequired,
};

export { Edit, EditErr };
